<template>
  <div class="ca-footer__brand px-size-0 py-size-5">
    <div class="ca-footer__brand-row gap-size-5">
      <div class="ca-footer__brand-divider mx-size-0 my-size-3"></div>
      <a :href="logoUrl" class="ca-footer__brand-logo">

        <LogoCA />
  </a>
      
      <div class="ca-footer__brand-right gap-size-5">
    

        <div class="ca-footer__social">
          <IconButton
            v-for="(social, idx) in socialLinks"
            :key="idx"
            :disabled="false"
            type="inverted"
            class="social-button"
            variant="tertiary"
            :aria-label="social.label"
            @clicked="navigateTo(social.url)"
          >
            <template #trailingIcon>
              <img
                v-if="social.icon"
                :src="social.icon"
                :alt="social.label"
              />
            </template>
          </IconButton>
        </div>

        <!-- Mobile: link to store. Desktop: opens modal -->
        <a
          :href="appStoreHref"
          class="ca-footer__app-link p-size-4 ca-footer__app-link--mobile gap-size-4 p-size-4"
        >
          <span class="ca-footer__app-link-text caption-action">{{
            appLink.text
          }}</span>
          <IconAppStoreDownload
            v-if="isIos"
            class="ca-footer__app-link-store"
          />
          <IconStoreDownload v-else class="ca-footer__app-link-store" />
        </a>

        <button
          class="ca-footer__app-link p-size-4 ca-footer__app-link--desktop"
          @click="showModal = true"
        >

          <span class="ca-footer__app-link-text caption-action">{{
            appLink.text
          }}</span>
          <img
            :src="appLink.qrCode?.src"
            :alt="appLink.qrCode?.alt || ''"
            class="ca-footer__app-link-icon"
          />
        </button>
      </div>
    </div>

    <CaAppModal
      :open="showModal"
      :title="appLink.modalTitle"
      :subtitle="appLink.modalSubtitle"
      :dividerText="appLink.modalDividerText"
      :closeLabel="appLink.modalCloseLabel"
      :qrCode="appLink.qrCode"
      :appStoreUrl="appLink.appStoreUrl"
      :playStoreUrl="appLink.playStoreUrl"
      :huaweiStoreUrl="appLink.huaweiStoreUrl"
      @close="showModal = false"
    />
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import CaAppModal from "../CaAppModal.vue";
import { IconButton } from "ca-online-design-system";
import LogoCA from "@assets/Logo_CA.svg";
import IconStoreDownload from "@assets/GooglePlay_Download_Btn-dark-PT.svg";
import IconAppStoreDownload from "@assets/AppStore_Download_Btn-Dark-PT.svg";

const props = defineProps({
  socialLinks: {
    type: Array,
    required: true,
  },
  appLink: {
    type: Object,
    required: true,
  },
  logoUrl: {
    type: String,
    default: "/particulares",
  },
});

const isIos = computed(() => {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  return /iPhone|iPad|iPod/i.test(ua);
});

const appStoreHref = computed(() => {
  return isIos.value ? props.appLink.appStoreUrl : props.appLink.playStoreUrl;
});

const showModal = ref(false);

function navigateTo(url) {
  window.location.href = url;
}
</script>

<style scoped lang="scss">
.social-button :deep(svg path) {
  stroke: none !important;
}

.social-button :deep(img) {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.ca-footer__brand-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;

  @media (min-width: 1025px) {
    flex-direction: row;
    align-items: center;
    align-self: stretch;
  }
}

.ca-footer__brand-logo {
  display: flex;
  align-items: center;
  order: 3;

  @media (min-width: 1025px) {
    order: 0;
  }
}

.ca-footer__brand-divider {
  order: 2;
  align-self: stretch;
  border-top: 0.5px solid var(--ca-color-text-light);

  @media (min-width: 1025px) {
    display: none;
  }
}

.ca-footer__brand-right {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  order: 1;
  width: 100%;

  @include ca-bp("sm") {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  @media (min-width: 1025px) {
    gap: var(--size-6);
    order: 0;
    width: auto;
    justify-content: flex-end;
  }
}

.ca-footer__social {
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  order: 1;

  @include ca-bp("sm") {
    order: 0;
    gap: var(--size-4);
  }

  @media (min-width: 1025px) {
    justify-content: flex-start;
    align-self: auto;
    gap: var(--size-4);
  }
}

.ca-footer__app-link {
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  background:
    linear-gradient(
      0deg,
      var(--ca-color-neutrals-inverted-10, rgba(255, 255, 255, 0.1)) 0%,
      var(--ca-color-neutrals-inverted-10, rgba(255, 255, 255, 0.1)) 100%
    ),
    var(--ca-color-neutrals-on-background, #333c45);
  border-radius: 12px;
  color: var(--ca-color-text-inverted);
  text-decoration: none;
  order: 2;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;

  @include ca-bp("sm") {
    order: 0;
    padding: var(--size-4);
    justify-content: flex-start;
    align-self: auto;
    gap: var(--size-4);
  }

  &:hover {
    background:
      linear-gradient(
        0deg,
        var(--ca-color-neutrals-inverted-30, rgba(255, 255, 255, 0.3)) 0%,
        var(--ca-color-neutrals-inverted-30, rgba(255, 255, 255, 0.3)) 100%
      ),
      var(--ca-color-neutrals-on-background, #333c45);
  }

  &:active {
    background: var(--ca-color-neutrals-inverted-60);
  }

  &:focus-visible {
    outline: 1px solid var(--ca-color-feedback-info);
    outline-offset: 1px;
  }
}

// Mobile variant: visible below 1025px, hidden on 1025px+
.ca-footer__app-link--mobile {
  display: flex;

  @media (min-width: 1025px) {
    display: none;
    padding: var(--size-3);
  }
}

// Desktop variant: hidden below 1025px, visible on 1025px+
.ca-footer__app-link--desktop {
  display: none;
  cursor: pointer;

  @media (min-width: 1025px) {
    display: flex;
    padding: var(--size-3);
  }
}

.ca-footer__app-link-text {
  text-align: left;

  @include ca-bp("sm") {
    width: 76px;
    padding: var(--size-3);
  }
}

.ca-footer__app-link-icon {
  display: none;

  @media (min-width: 1025px) {
    display: flex;
  }
}

.ca-footer__app-link-store {
  display: block;
}
</style>
